import PropTypes from 'prop-types';
import BetslipViewImpl from './betslip-view.js';
import MojitoCore from 'mojito/core';
import { noop } from 'mojito/utils';

const { UIView } = MojitoCore.Presentation;

/**
 * Betslip view displaying betslip data.
 *
 * @class BetslipView
 * @memberof Mojito.Modules.Betslip
 */

/**
 * BetslipView prop types.
 *
 * @property {Mojito.Services.Betslip.types.Betslip} [betslip] - Betslip object.
 * @property {string} [currencyCode] - Currency code to use in betslip.
 * @property {Mojito.Services.Bonus.types.FreeBetsTypes[]} [freeBets] - List of free bets to display.
 * @property {boolean} [hasPendingBonus = false] - Set to true if some bonus requests are pending (so bonuses info will be inactive).
 * @property {boolean} [betPlacementButtonDisabled] - Bet placement button state override.
 * @property {boolean} [hasOddsChangeBet = false] - Flag to indicate if betslip contains any unaccepted odds changes.
 * @property {boolean} [hasHcapChangeBet = false] - Flag to indicate if betslip contains any unaccepted handicap changes.
 * @property {boolean} [hasBoostedHcapChange = false] - Flag to indicate if betslip contains any unaccepted boosted handicap changes.
 * @property {string} [stakeGroup] - Stake Group name.
 * @property {boolean} [isStakeGroupPending = false] - True is stake group activation is in progress, false otherwise.
 * @property {boolean} [hasPendingBankerBet = false] - True is banker bet set/unset is in progress, false otherwise.
 * @property {boolean} [showReceipt] - Flag to show receipt.
 * @property {boolean} [isSuspended = false] - Flag to suspend place bet content.
 * @property {boolean} [isExpired = false] - Flag to disable bet content due to bet expiration.
 * @property {Mojito.Services.Betslip.types.BETSLIP_STATUS} [betslipStatus] - Betslip status.
 * @property {Array<Mojito.Services.Betslip.types.Error>} [validationErrors] - List of betslip validation errors.
 * @property {Function} [onClearReceipt = () => {}] - Callback on receipt clear.
 * @property {Function} [onAcceptTechnicalError = () => {}] - Callback is getting triggered once user accepts unexpected technical error.
 * @property {boolean} [showOverask] - Flag to show overask.
 * @property {boolean} [bankersActivated] - Flag to indicate if bankers mode is activated.
 * @property {Mojito.Services.Betslip.types.COMMIT_BETSLIP_MODE} [commitBetslipMode] - Commit betslip mode, indicating whether user can place a bet or save for later placement in a shop.
 *
 * @memberof Mojito.Modules.Betslip.BetslipView
 */
const propTypes = {
    betslip: PropTypes.object,
    currencyCode: PropTypes.string,
    freeBets: PropTypes.array,
    hasPendingBonus: PropTypes.bool,
    betPlacementButtonDisabled: PropTypes.bool,
    hasOddsChangeBet: PropTypes.bool,
    hasHcapChangeBet: PropTypes.bool,
    hasBoostedHcapChange: PropTypes.bool,
    stakeGroup: PropTypes.string,
    isStakeGroupPending: PropTypes.bool,
    hasPendingBankerBet: PropTypes.bool,
    showReceipt: PropTypes.bool,
    isSuspended: PropTypes.bool,
    isExpired: PropTypes.bool,
    betslipStatus: PropTypes.string,
    validationErrors: PropTypes.array,
    onClearReceipt: PropTypes.func,
    onAcceptTechnicalError: PropTypes.func,
    showOverask: PropTypes.bool,
    bankersActivated: PropTypes.bool,
    commitBetslipMode: PropTypes.string,
};

const defaultProps = {
    hasPendingBonus: false,
    hasOddsChangeBet: false,
    hasHcapChangeBet: false,
    hasBoostedHcapChange: false,
    isSuspended: false,
    isExpired: false,
    isStakeGroupPending: false,
    hasPendingBankerBet: false,
    onClearReceipt: noop,
    onAcceptTechnicalError: noop,
};

export default UIView('BetslipView', BetslipViewImpl, propTypes, defaultProps);
